import { isMobileViewport } from '@/hooks/use-mobile';
import { isPhoneDevice } from './layout-preference';

/** The one reseller whose users get Chekkit's Intercom support. */
export const INTERCOM_ALLOWED_RESELLER = '69cd40a0f71a2700029361c6';

const BUBBLE_HIDDEN_KEY = 'intercom-bubble-hidden';

type IntercomFn = (command: string, options?: Record<string, unknown>) => void;
const intercom = () => (window as unknown as { Intercom?: IntercomFn }).Intercom;

/** Whether this account gets the Help & support (Intercom) entry. */
export const canOpenSupport = (auth?: { reseller?: string } | null) =>
	!auth?.reseller || auth.reseller === INTERCOM_ALLOWED_RESELLER;

/** Opens the Intercom messenger; full screen on phones. */
export const openSupport = () => {
	const fn = intercom();
	if (typeof fn === 'function') fn('show');
	else window.location.href = 'mailto:support@chekkit.io';
};

export const isBubbleHiddenByUser = () => {
	try {
		return localStorage.getItem(BUBBLE_HIDDEN_KEY) === 'true';
	} catch {
		return false;
	}
};

export const setBubbleHiddenByUser = (hidden: boolean) => {
	try {
		localStorage.setItem(BUBBLE_HIDDEN_KEY, String(hidden));
	} catch {
		/* storage may be disabled */
	}
};

// Full-screen flows holding the launcher hidden (see holdLauncherHidden).
let launcherHolds = 0;

/**
 * Phones never show the floating Intercom bubble, in either orientation: it
 * would sit over the composer and the tab bar, and in landscape over the
 * middle of a ~390px-tall screen. Support opens from the More drawer or the
 * account menu's Get support instead.
 */
export const shouldHideLauncher = () =>
	launcherHolds > 0 || isMobileViewport() || isPhoneDevice() || isBubbleHiddenByUser();

/** Re-applies the launcher visibility, e.g. after crossing the phone breakpoint. */
export const syncLauncherVisibility = () => {
	const fn = intercom();
	if (typeof fn === 'function') fn('update', { hide_default_launcher: shouldHideLauncher() });
};

/**
 * Keeps the launcher hidden while a full-screen flow (the AI Employee setup
 * wizard) is open, even when something re-syncs it meanwhile: Layout booting
 * Intercom, or the user menu remounting after a resize across 768px.
 * Returns the release, which puts the launcher back as it should be.
 */
export const holdLauncherHidden = () => {
	launcherHolds += 1;
	syncLauncherVisibility();
	let released = false;
	return () => {
		if (released) return;
		released = true;
		launcherHolds -= 1;
		syncLauncherVisibility();
	};
};
